<template>
  <div class="fv-page">
    <!-- ==================== 概览头 ==================== -->
    <div class="fv-hero">
      <div class="fv-hero-l">
        <div class="fv-hero-title">
          <el-button icon="ArrowLeft" text @click="go({ path: '/formList' })">返回</el-button>
          <h2>核销记录</h2>
          <span class="fv-hero-badge">核销凭证</span>
        </div>
        <p class="fv-hero-sub">
          核销码是表单提交成功后发给家长的凭证，员工当面扫码或输码完成核销；
          已核销 / 已作废的记录不再出二维码。
        </p>
        <div class="fv-ov">
          <div class="fv-ov-item">
            <b class="c-pending">{{ ovText(ov.pending) }}</b><span>未核销</span>
          </div>
          <div class="fv-ov-item">
            <b class="c-done">{{ ovText(ov.done) }}</b><span>已核销</span>
          </div>
          <div class="fv-ov-item">
            <b class="c-void">{{ ovText(ov.voided) }}</b><span>已作废</span>
          </div>
          <div class="fv-ov-item">
            <b class="c-total">{{ ovText(ov.total) }}</b><span>合计</span>
          </div>
        </div>
      </div>
      <div class="fv-hero-r">
        <el-button icon="Refresh" @click="refresh">刷 新</el-button>
        <el-button type="primary" icon="Postcard" @click="batchReissue">按表单批量补发核销码</el-button>
      </div>
    </div>

    <!-- ==================== 筛选条 ==================== -->
    <div class="fv-bar">
      <el-select v-model="query.formId" clearable filterable placeholder="全部表单" class="fv-sel-form">
        <el-option v-for="f in formOptions" :key="f.id" :label="f.title" :value="f.id"></el-option>
      </el-select>
      <el-select v-model="query.verifyState" clearable placeholder="全部状态" class="fv-sel-state">
        <el-option v-for="s in statusOptions" :key="s.value" :label="s.label" :value="s.value"></el-option>
      </el-select>
      <el-input
          v-model="query.keyword"
          clearable
          placeholder="姓名 / 手机号 / 核销码"
          prefix-icon="Search"
          class="fv-keyword"
          @keyup.enter="search"></el-input>
      <el-button type="primary" icon="Search" @click="search">查询</el-button>
      <el-button icon="RefreshLeft" @click="reset">重置</el-button>
      <span class="fv-count">共 {{ pageTotal }} 条</span>
    </div>

    <!-- ==================== 历史报名「零码」提示（P2-5） ==================== -->
    <div v-if="noCodeHint" class="fv-nocode">
      <el-icon class="fv-nocode-ic"><WarningFilled /></el-icon>
      <span class="fv-nocode-txt">
        该表单已有报名记录，但还没有任何核销码 —— 说明核销能力是在这批报名之后才开启的，
        家长端「我的凭证」会是空的。请点右侧「按表单批量补发核销码」为存量报名补码；
        <b>补发不会覆盖已有码</b>（已打码的记录原样保留）。
      </span>
      <el-button type="warning" icon="Postcard" @click="batchReissue">按表单批量补发</el-button>
    </div>

    <!-- ==================== 表格 ==================== -->
    <div class="fv-card">
      <el-table
          v-loading="listLoading"
          :data="tableData"
          :cell-style="{padding: '6px 0'}"
          row-key="id"
          class="fv-table">
        <el-table-column min-width="180" show-overflow-tooltip label="表单">
          <template #default="scope">{{ scope.row.formTitle || '—' }}</template>
        </el-table-column>
        <el-table-column width="110" label="学员">
          <template #default="scope">{{ scope.row.studentName || '—' }}</template>
        </el-table-column>
        <el-table-column width="140" label="手机号">
          <template #default="scope">{{ maskPhone(scope.row.phone) }}</template>
        </el-table-column>
        <el-table-column min-width="190" label="核销码">
          <template #default="scope">
            <span v-if="scope.row.verifyCode" class="fv-code">{{ scope.row.verifyCode }}</span>
            <span v-else class="fv-dash">—</span>
            <el-button
                v-if="scope.row.verifyCode"
                size="small"
                text
                type="primary"
                icon="CopyDocument"
                @click="copyText(scope.row.verifyCode, '核销码已复制')">复制</el-button>
          </template>
        </el-table-column>
        <el-table-column width="90" align="center" label="状态">
          <template #default="scope">
            <el-tag :type="verifyTagType(scope.row.verifyState)" size="small">
              {{ verifyStateText(scope.row.verifyState) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column width="160" label="核销时间">
          <template #default="scope">{{ scope.row.verifyAt || '—' }}</template>
        </el-table-column>
        <el-table-column width="110" label="核销人">
          <template #default="scope">{{ scope.row.verifyByName || '—' }}</template>
        </el-table-column>
        <el-table-column width="160" label="提交时间">
          <template #default="scope">{{ scope.row.createTime || '—' }}</template>
        </el-table-column>
        <el-table-column width="250" align="center" fixed="right" label="操作">
          <template #default="scope">
            <el-button size="small" text type="primary" @click="openQr(scope.row)">查看二维码</el-button>
            <template v-if="scope.row.verifyState === 0">
              <el-button size="small" text type="primary" @click="doReissue(scope.row)">补发核销码</el-button>
              <el-popconfirm
                  title="确定作废该核销码吗？作废后家长无法再扫码核销。"
                  confirm-button-text="作废"
                  cancel-button-text="取消"
                  @confirm="doVoid(scope.row)">
                <template #reference>
                  <el-button size="small" text type="danger">作废</el-button>
                </template>
              </el-popconfirm>
            </template>
          </template>
        </el-table-column>

        <!-- 空态 / 失败态分开：失败给「重试」，不留一个点了没反应的按钮 -->
        <template #empty>
          <div v-if="listFailed" class="fv-fail">
            <el-icon><WarningFilled /></el-icon>
            <p>{{ listError || '核销记录加载失败' }}</p>
            <el-button type="primary" icon="RefreshRight" @click="selectList">重试</el-button>
          </div>
          <div v-else class="fv-empty">
            <el-icon><Ticket /></el-icon>
            <p>{{ emptyText }}</p>
            <el-button v-if="hasFilter" text type="primary" @click="reset">重置筛选</el-button>
          </div>
        </template>
      </el-table>
    </div>

    <div class="pagination">
      <el-pagination
          background
          layout="total, sizes, prev, pager, next, jumper"
          :page-sizes="[10, 20, 50, 100]"
          :current-page="query.pageIndex"
          :page-size="query.pageSize"
          :total="pageTotal"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"></el-pagination>
    </div>

    <!-- ==================== 二维码弹窗 ==================== -->
    <el-dialog v-model="qrVisible" title="核销二维码" width="420px" align-center>
      <div class="fv-qr">
        <img v-if="qrRow && qrRow.qrDataUrl" :src="qrRow.qrDataUrl" class="fv-qr-img" alt="核销二维码"/>
        <div v-else class="fv-qr-empty">该记录未生成二维码（已核销 / 已作废，或超出单页出图上限）</div>
        <div v-if="qrRow" class="fv-qr-code">{{ qrRow.verifyCode || '——' }}</div>
        <div v-if="qrRow" class="fv-qr-meta">
          {{ qrRow.formTitle || '—' }} · {{ qrRow.studentName || '—' }}
        </div>
        <div class="fv-qr-tip">核销码是提交成功后发给家长的凭证，员工当面扫码或输码完成核销。</div>
      </div>
      <template #footer>
        <el-button
            :disabled="!qrRow || !qrRow.verifyCode"
            @click="copyText(qrRow.verifyCode, '核销码已复制')">复制码</el-button>
        <el-button type="primary" @click="qrVisible = false">关 闭</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import {
  formPage,
  formVerifyPage,
  formVerifyVoid,
  formVerifyReissue,
  formRecordPage,
  VERIFY_STATE_TEXT,
} from '@/api/form';

export default {
  name: 'FormVerify',
  data() {
    return {
      listLoading: false,
      listFailed: false,
      listError: '',
      // ★ P2-5：该表单「有报名记录但一个核销码都没有」→ 顶部黄条提示运营补发
      noCodeHint: false,
      tableData: [],
      pageTotal: 0,
      formOptions: [],
      // 概览：4 个数字分别取一次 total；取不到时置 null → 显示「—」（不骗人）
      ov: { pending: null, done: null, voided: null, total: null },
      query: {
        pageIndex: 1,
        pageSize: 10,
        formId: null,
        verifyState: null,
        keyword: '',
      },
      qrVisible: false,
      qrRow: null,
    };
  },
  computed: {
    statusOptions() {
      return Object.keys(VERIFY_STATE_TEXT).map(k => ({
        value: Number(k),
        label: VERIFY_STATE_TEXT[k],
      }));
    },
    hasFilter() {
      return this.query.formId != null
          || this.query.verifyState != null
          || !!this.query.keyword;
    },
    emptyText() {
      if (this.hasFilter) return '没有符合筛选条件的核销记录';
      return '还没有核销记录 —— 表单提交成功后会为家长生成核销码';
    },
  },
  methods: {
    /* ---------------- 展示口径 ---------------- */
    verifyStateText(state) {
      return VERIFY_STATE_TEXT[state] || '未知';
    },
    verifyTagType(state) {
      if (state === 1) return 'success';
      if (state === 2) return 'info';
      return 'warning';
    },
    /** 概览数字：null 显示「—」，不要用 0 冒充（后端没返回 ≠ 真的是 0） */
    ovText(v) {
      return (v === null || v === undefined) ? '—' : v;
    },
    /** 手机号脱敏：11 位手机号中间四位打码；其它格式原样返回 */
    maskPhone(phone) {
      const s = String(phone == null ? '' : phone);
      if (/^\d{11}$/.test(s)) return s.slice(0, 3) + '****' + s.slice(7);
      return s || '—';
    },
    /* ---------------- 数据 ---------------- */
    loadFormOptions() {
      formPage({ pageIndex: 1, pageSize: 200, keyword: '' }).then(val => {
        if (val && val.code === 200) {
          const records = (val.data && val.data.records) || [];
          this.formOptions = records.map(r => ({ id: r.id, title: r.title || ('#' + r.id) }));
        }
      }).catch(() => { /* 下拉拉取失败不影响主列表 */ });
    },
    /**
     * 概览：不使用当前页数据累加（那只会算出一页的数字、骗人）。
     * 用同一个分页接口，pageSize:1，分别带 verifyState=0/1/2 与「不传状态」各取一次 total，
     * 并发 4 次。任一次非 200 / 抛错 → 对应数字置 null，页面显示「—」。
     * 口径：跟随「表单 / 关键字」筛选，但**不受核销状态筛选影响**（否则四个数字会互相塌陷）。
     */
    loadOverview() {
      const base = { pageIndex: 1, pageSize: 1 };
      if (this.query.formId != null) base.formId = this.query.formId;
      if (this.query.keyword) base.keyword = this.query.keyword;
      const one = (verifyState) => {
        const param = Object.assign({}, base);
        if (verifyState != null) param.verifyState = verifyState;
        return formVerifyPage(param).then(val => {
          if (val && val.code === 200 && val.data) return Number(val.data.total || 0);
          return null;
        }).catch(() => null);
      };
      // ★ P2-5：该表单的**报名记录总数**（不带核销状态过滤；未选表单时视为 0）。
      //   核销记录分页只含「已有码」的记录 ⇒ 与它对比即可判定「有报名、但零码」。
      const recordTotal = () => {
        if (this.query.formId == null) return Promise.resolve(0);
        return formRecordPage({ pageIndex: 1, pageSize: 1, formId: this.query.formId })
            .then(val => (val && val.code === 200 && val.data) ? Number(val.data.total || 0) : 0)
            .catch(() => 0);
      };
      Promise.all([one(null), one(0), one(1), one(2), recordTotal()])
          .then(([total, pending, done, voided, recTotal]) => {
            this.ov = { total, pending, done, voided };
            // ★ P2-5 黄条判据：选了表单 + 无关键字筛选 + 该表单有报名记录 + 一个核销码都没有。
            //   核销记录分页只包含「已有码」的记录 ⇒ 这里的 0 就是真的「零码」。
            //   ⚠ 必须排除关键字筛选：那时 total 只是被筛掉的子集，会误报「零码」。
            this.noCodeHint = this.query.formId != null && !this.query.keyword
                && recTotal > 0 && total === 0;
          });
    },
    selectList() {
      this.listLoading = true;
      this.listFailed = false;
      this.listError = '';
      formVerifyPage(this.query).then(val => {
        this.listLoading = false;
        if (val && val.code === 200) {
          this.tableData = (val.data && val.data.records) || [];
          this.pageTotal = (val.data && val.data.total) || 0;
        } else {
          // 业务失败：清空列表 + 置失败态（表格 #empty 里给「重试」），不能只关 loading
          this.tableData = [];
          this.pageTotal = 0;
          this.listFailed = true;
          this.listError = (val && val.message) || '核销记录加载失败';
        }
      }).catch((err) => {
        this.listLoading = false;
        this.tableData = [];
        this.pageTotal = 0;
        this.listFailed = true;
        this.listError = (err && err.message) || '核销记录加载失败，请检查网络';
      });
    },
    refresh() {
      this.selectList();
      this.loadOverview();
    },
    search() {
      this.query.pageIndex = 1;
      this.refresh();
    },
    reset() {
      this.query.formId = null;
      this.query.verifyState = null;
      this.query.keyword = '';
      this.query.pageIndex = 1;
      this.refresh();
    },
    handleSizeChange(size) {
      this.query.pageSize = size;
      this.query.pageIndex = 1;
      this.selectList();
    },
    handlePageChange(index) {
      this.query.pageIndex = index;
      this.selectList();
    },
    /* ---------------- 操作 ---------------- */
    openQr(row) {
      this.qrRow = row;
      this.qrVisible = true;
    },
    doVoid(row) {
      formVerifyVoid(row.id).then(val => {
        if (val && val.code === 200) {
          this.$message.success('已作废');
          this.refresh();
        } else {
          this.$message.warning((val && val.message) || '作废失败');
        }
      }).catch(() => {
        this.$message.error('作废失败，请稍后重试');
      });
    },
    doReissue(row) {
      formVerifyReissue({ id: row.id }).then(val => {
        if (val && val.code === 200) {
          this.$message.success((val && val.message) || '已补发核销码');
          this.refresh();
        } else {
          this.$message.warning((val && val.message) || '补发失败');
        }
      }).catch(() => {
        this.$message.error('补发失败，请稍后重试');
      });
    },
    /** 按表单批量补发：必须先在筛选条选一张表单（接口按 formId 批量） */
    batchReissue() {
      if (this.query.formId == null) {
        this.$message.warning('请先在上方「表单」里选择一张表单，再批量补发');
        return;
      }
      const title = (this.formOptions.find(f => f.id === this.query.formId) || {}).title || '该表单';
      this.$confirm(
          `确定给「${title}」下所有尚未打码的记录生成核销码吗？\n已生成过的记录不会重复补发。`,
          '批量补发核销码',
          { type: 'warning' },
      ).then(() => {
        formVerifyReissue({ formId: this.query.formId }).then(val => {
          if (val && val.code === 200) {
            const n = val.data && val.data.count;
            this.$message.success((val && val.message) || (n != null ? `已补发 ${n} 条` : '已补发'));
            this.refresh();
          } else {
            this.$message.warning((val && val.message) || '批量补发失败');
          }
        }).catch(() => {
          this.$message.error('批量补发失败，请稍后重试');
        });
      }).catch(() => {});
    },
    /* ---------------- 通用 ---------------- */
    /** 与 FormList 同款安全跳转：push 失败要弹原因，不静默 */
    go(target) {
      const p = this.$router.push(target);
      if (p && typeof p.catch === 'function') {
        p.catch(err => {
          const msg = (err && err.message) || '未知错误';
          this.$message.error(`打开页面失败：${msg}（若是改版后首次进入，请按 Ctrl+F5 强制刷新）`);
        });
      }
    },
    /** 与 FormList / FormData 同款：clipboard API + textarea 兜底 */
    copyText(text, tip) {
      const done = () => this.$message.success(tip || '已复制');
      const fallback = () => {
        try {
          const ta = document.createElement('textarea');
          ta.value = text;
          ta.style.position = 'fixed';
          ta.style.opacity = '0';
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
          done();
        } catch (e) {
          this.$message.warning('复制失败，请手动复制');
        }
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(done).catch(fallback);
      } else {
        fallback();
      }
    },
  },
  created() {
    // 支持从列表页跳转带 formId 进来；没带也不拦——表单选择器常驻在筛选条里，不是死胡同
    const q = this.$route.query.formId;
    this.query.formId = (q != null && q !== '' && !Number.isNaN(Number(q))) ? Number(q) : null;
    this.loadFormOptions();
    this.selectList();
    this.loadOverview();
  },
};
</script>

<style scoped>

/* @form-tokens:start */
/* ======================= 表单模块设计令牌（v3.11 视觉统一）=======================
 * ★ 本块在多个表单页各存一份（CSS 变量按作用域隔离，无法跨 scoped 共享）。
 *   改配色的唯一入口 = D:/tmp/shot/form_tokens.py 的 TOKEN_BLOCK，改完重跑该脚本
 *   一键同步；**不要手改单页**，否则多份会漂移。
 * ★ 为什么不放全局 :root：--el-color-primary 一旦全局生效会波及后台所有模块
 *   （订单 / 教练 / 财务…）。本模块只对「表单」相关页负责，故挂在页面根容器上，
 *   作用域精确、零外溢。
 * ========================================================================== */
.fv-page {
  --el-color-primary: #8dc63f;
  --el-color-primary-light-3: #afd779;
  --el-color-primary-light-5: #c6e39f;
  --el-color-primary-light-7: #ddeec5;
  --el-color-primary-light-8: #e8f4d9;
  --el-color-primary-light-9: #f4f9ec;
  --el-color-primary-dark-2: #719e32;

  /* —— 文字五档（严格对齐 Element 灰阶） —— */
  --yh-t1: #303133; /* 主文字 */
  --yh-t2: #606266; /* 常规 */
  --yh-t3: #909399; /* 次要 */
  --yh-t4: #a8abb2; /* 占位 */
  --yh-t5: #c0c4cc; /* 禁用 */

  /* —— 主色四档 —— */
  --yh-theme: #8dc63f;
  --yh-theme-d: #7cad37;
  --yh-theme-dd: #5f9427;
  --yh-theme-l: #eef7e2;
  --yh-theme-ll: #f6fbef;

  /* —— 面与线 —— */
  --yh-bg: #f5f7fa;
  --yh-fill: #fafbfc;
  --yh-line: #ebeef5;
  --yh-line-d: #dcdfe6;
  --yh-divide: #f2f3f5;

  /* —— 圆角五档 —— */
  --yh-r-xs: 4px;
  --yh-r-sm: 6px;
  --yh-r: 8px;
  --yh-r-lg: 12px;
  --yh-r-pill: 999px;
}
/* @form-tokens:end */

.fv-page {
  padding: 16px 20px 30px;
  min-height: 100%;
  background-color: #f5f7fa;
  box-sizing: border-box;
}

/* ==================== 概览头 ==================== */
.fv-hero {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
  background: #fff;
  border-radius: var(--yh-r-lg);
  padding: 16px 20px;
  margin-bottom: 14px;
  box-shadow: 0 1px 4px rgba(0, 0, 21, 0.06);
}

.fv-hero-title {
  display: flex;
  align-items: center;
  gap: 8px;
}

.fv-hero-title h2 {
  margin: 0;
  font-size: 19px;
  font-weight: 600;
  color: var(--yh-t1);
}

.fv-hero-badge {
  padding: 1px 8px;
  border-radius: var(--yh-r-pill);
  background: var(--yh-theme-l);
  color: var(--yh-theme-dd);
  font-size: 12px;
}

.fv-hero-sub {
  margin: 6px 0 0;
  max-width: 760px;
  font-size: 12px;
  line-height: 1.8;
  color: var(--yh-t3);
}

.fv-ov {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.fv-ov-item {
  display: flex;
  align-items: baseline;
  gap: 4px;
  padding: 4px 10px;
  border-radius: var(--yh-r);
  background: var(--yh-fill);
}

.fv-ov-item b {
  font-size: 15px;
  color: var(--yh-t1);
}

.fv-ov-item span {
  font-size: 12px;
  color: var(--yh-t3);
}

.c-pending { color: #e6a23c !important; }
.c-done { color: #67c23a !important; }
.c-void { color: var(--yh-t3) !important; }
.c-total { color: var(--yh-theme) !important; }

.fv-hero-r {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  flex: 0 0 auto;
}

/* ==================== 筛选条 ==================== */
.fv-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  background: #fff;
  border-radius: var(--yh-r-lg);
  padding: 12px 16px;
  margin-bottom: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 21, 0.06);
}

.fv-sel-form {
  width: 220px;
}

.fv-sel-state {
  width: 130px;
}

.fv-keyword {
  width: 220px;
}

.fv-count {
  margin-left: auto;
  font-size: 12px;
  color: var(--yh-t3);
}

/* ==================== 历史报名「零码」提示（P2-5） ==================== */
.fv-nocode {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fdf6ec;
  border: 1px solid #f5dab1;
  border-radius: var(--yh-r-lg);
  padding: 10px 16px;
  margin-bottom: 12px;
}

.fv-nocode-ic {
  flex: 0 0 auto;
  font-size: 18px;
  color: #e6a23c;
}

.fv-nocode-txt {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 13px;
  line-height: 1.7;
  color: #b88230;
}

.fv-nocode-txt b {
  color: #a86616;
}

/* ==================== 表格卡 ==================== */
.fv-card {
  background: #fff;
  border-radius: var(--yh-r-lg);
  padding: 6px 12px 14px;
  box-shadow: 0 1px 4px rgba(0, 0, 21, 0.06);
}

.fv-table :deep(.el-table__inner-wrapper::before) {
  display: none;
}

.fv-table :deep(.el-table__header th) {
  background: var(--yh-fill);
  color: var(--yh-t2);
  font-weight: 600;
}

.fv-table :deep(.el-table__row:hover > td) {
  background: var(--yh-theme-ll) !important;
}

.fv-code {
  font-family: Consolas, Monaco, monospace;
  font-weight: 600;
  color: var(--yh-t1);
  letter-spacing: 0.5px;
}

.fv-dash {
  color: var(--yh-t5);
}

.pagination {
  margin-top: 16px;
  text-align: right;
}

/* 空态 / 失败态 */
.fv-empty,
.fv-fail {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 26px 0 12px;
}

.fv-empty .el-icon,
.fv-fail .el-icon {
  font-size: 32px;
  color: #dcdfe6;
}

.fv-fail .el-icon {
  color: #e6a23c;
}

.fv-empty p,
.fv-fail p {
  margin: 0;
  font-size: 13px;
  color: var(--yh-t4);
}

/* ==================== 二维码弹窗 ==================== */
.fv-qr {
  text-align: center;
}

.fv-qr-img {
  width: 240px;
  height: 240px;
  object-fit: contain;
  border: 1px solid var(--yh-line);
  border-radius: var(--yh-r-sm);
  background: #fff;
}

.fv-qr-empty {
  padding: 70px 20px;
  color: var(--yh-t3);
  font-size: 13px;
  line-height: 1.7;
}

.fv-qr-code {
  margin-top: 12px;
  font-family: Consolas, Monaco, monospace;
  font-size: 22px;
  font-weight: 700;
  color: var(--yh-t1);
  letter-spacing: 2px;
}

.fv-qr-meta {
  margin-top: 6px;
  font-size: 12px;
  color: var(--yh-t3);
}

.fv-qr-tip {
  margin-top: 10px;
  font-size: 12px;
  line-height: 1.7;
  color: var(--yh-t4);
}
</style>
